import { action, computed, makeObservable, observable } from "mobx";
import type { ChangeEvent } from "react";
import { inject, injectable } from "tsyringe";
import {
  type AuthUser,
  type AuthIdentity,
  type IWarehouseApi,
  WAREHOUSE_API_TOKEN,
} from "../../api/WarehouseApi";
import { ViewModel } from "../../lib/vvm";
import {
  type INavigationStore,
  NAVIGATION_STORE_TOKEN,
  Screens,
} from "../../stores/navigation";
import { getStoredUser } from "../../utils/authStorage";
import {
  getTelegramWebAppInitData,
  isTelegramMiniAppEnvironment,
  type TelegramWidgetAuthUser,
} from "../../utils/telegramWebApp";
import {
  getAuthIdentityProviderLabel,
  sortAuthIdentities,
} from "./viewmodel/identities";
import {
  hasEmailFormChanges,
  hasProfileFormChanges,
} from "./viewmodel/profile-state";
import {
  clearAccountMessages,
  linkAppleIdentity,
  linkGoogleIdentity,
  linkTelegramMiniAppIdentity,
  linkTelegramWidgetIdentity,
  refreshAccountData,
  saveEmailIdentity,
  saveProfileChanges,
  setProviderAuthError,
  type AccountOperationState,
  unlinkAuthIdentity,
} from "./viewmodel/account-operations";

@injectable()
export class AccountPageViewModel extends ViewModel {
  @observable.shallow
  identities: AuthIdentity[] = [];

  @observable
  isLoading = false;

  @observable
  error: string | null = null;

  @observable
  success: string | null = null;

  @observable
  profileNameInput = "";

  @observable
  profileEmailInput = "";

  @observable
  profilePhotoInput = "";

  @observable.ref
  profile: AuthUser | null = null;

  @observable
  telegramInitDataInput = "";

  @observable
  processingProvider: AuthIdentity["provider"] | null = null;

  @observable
  isLinkingEmail = false;

  @observable
  isLinkingTelegram = false;

  @observable
  isLinkingGoogle = false;

  @observable
  isLinkingApple = false;

  @observable
  isSavingProfile = false;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();
    makeObservable(this);
  }

  protected onViewMounted(): void {
    this.syncTelegramInitData();
    void this.refresh();
  }

  @computed
  get user() {
    return this.profile || getStoredUser();
  }

  @computed
  get isAdmin(): boolean {
    return this.user?.role === "admin";
  }

  @computed
  get pageSubtitle(): string {
    const user = this.user;
    if (!user) {
      return "данные пользователя недоступны";
    }

    return `${user.name} · ${user.role || "user"}`;
  }

  @computed
  get hasEmailIdentity(): boolean {
    return this.identities.some((item) => item.provider === "email");
  }

  @computed
  get hasProfileChanges(): boolean {
    return hasProfileFormChanges({
      profile: this.profile,
      profileNameInput: this.profileNameInput,
      profilePhotoInput: this.profilePhotoInput,
    });
  }

  @computed
  get canSaveProfile(): boolean {
    return (
      !this.isLoading &&
      !this.isSavingProfile &&
      this.profileNameInput.trim().length > 0 &&
      this.hasProfileChanges
    );
  }

  @computed
  get hasEmailChanges(): boolean {
    return hasEmailFormChanges({
      profile: this.profile,
      profileEmailInput: this.profileEmailInput,
    });
  }

  @computed
  get canSaveEmail(): boolean {
    return (
      !this.isLoading &&
      !this.isLinkingEmail &&
      this.hasEmailChanges &&
      this.profileEmailInput.trim().length > 4 &&
      this.profileEmailInput.includes("@")
    );
  }

  @computed
  get canLinkTelegram(): boolean {
    return (
      !this.isLoading &&
      !this.isLinkingTelegram &&
      this.telegramInitDataInput.trim().length >= 10
    );
  }

  @computed
  get isTelegramMiniApp(): boolean {
    return isTelegramMiniAppEnvironment();
  }

  @computed
  get telegramBotUsername(): string {
    return String(import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "").trim();
  }

  @computed
  get canUseTelegramWidget(): boolean {
    return (
      !this.isTelegramMiniApp &&
      Boolean(this.telegramBotUsername) &&
      !this.isLoading &&
      !this.isLinkingTelegram &&
      !this.isLinkingGoogle &&
      !this.isLinkingApple
    );
  }

  @computed
  get googleClientId(): string {
    return String(import.meta.env.VITE_GOOGLE_CLIENT_ID || "").trim();
  }

  @computed
  get appleClientId(): string {
    return String(import.meta.env.VITE_APPLE_CLIENT_ID || "").trim();
  }

  @computed
  get appleRedirectUri(): string {
    return String(import.meta.env.VITE_APPLE_REDIRECT_URI || "").trim();
  }

  @computed
  get canUseGoogleWidget(): boolean {
    return (
      !this.isTelegramMiniApp &&
      Boolean(this.googleClientId) &&
      !this.isLoading &&
      !this.isLinkingGoogle &&
      !this.isLinkingTelegram &&
      !this.isLinkingApple
    );
  }

  @computed
  get canUseAppleWidget(): boolean {
    return (
      !this.isTelegramMiniApp &&
      Boolean(this.appleClientId) &&
      !this.isLoading &&
      !this.isLinkingApple &&
      !this.isLinkingGoogle &&
      !this.isLinkingTelegram
    );
  }

  @computed
  get canLinkTelegramMiniApp(): boolean {
    return (
      this.isTelegramMiniApp &&
      this.canLinkTelegram &&
      !this.isLinkingGoogle &&
      !this.isLinkingApple
    );
  }

  @computed
  get sortedIdentities(): AuthIdentity[] {
    return sortAuthIdentities(this.identities);
  }

  @action.bound
  goBack(): void {
    this.navigationStore.setScreen(Screens.Main);
  }

  @action.bound
  onEmailInputChange(
    event: ChangeEvent<HTMLInputElement>,
    data: { value: string },
  ): void {
    this.profileEmailInput = data.value || event.target.value || "";
  }

  @action.bound
  onProfileNameInputChange(
    event: ChangeEvent<HTMLInputElement>,
    data: { value: string },
  ): void {
    this.profileNameInput = data.value || event.target.value || "";
  }

  @action.bound
  onProfilePhotoInputChange(
    event: ChangeEvent<HTMLInputElement>,
    data: { value: string },
  ): void {
    this.profilePhotoInput = data.value || event.target.value || "";
  }

  @action.bound
  syncTelegramInitData(): void {
    this.telegramInitDataInput = getTelegramWebAppInitData();
  }

  @action.bound
  clearMessages(): void {
    clearAccountMessages(this.operationState);
  }

  @action.bound
  onProviderAuthError(message: string): void {
    setProviderAuthError(this.operationState, message);
  }

  @action.bound
  async refresh(): Promise<void> {
    await refreshAccountData({
      state: this.operationState,
      api: this.api,
      getCurrentUserId: () => this.getCurrentUserId(),
    });
  }

  @action.bound
  async saveProfile(): Promise<void> {
    await saveProfileChanges({
      state: this.operationState,
      api: this.api,
      getCurrentUserId: () => this.getCurrentUserId(),
    });
  }

  @action.bound
  async saveEmail(): Promise<void> {
    await saveEmailIdentity({
      state: this.operationState,
      api: this.api,
      getCurrentUserId: () => this.getCurrentUserId(),
    });
  }

  @action.bound
  async linkTelegram(): Promise<void> {
    this.syncTelegramInitData();
    await linkTelegramMiniAppIdentity({
      state: this.operationState,
      api: this.api,
      refresh: () => this.refresh(),
    });
  }

  @action.bound
  async linkTelegramFromWidget(payload: TelegramWidgetAuthUser): Promise<void> {
    await linkTelegramWidgetIdentity({
      state: this.operationState,
      api: this.api,
      refresh: () => this.refresh(),
      payload,
    });
  }

  @action.bound
  async linkGoogleFromCredential(idToken: string): Promise<void> {
    await linkGoogleIdentity({
      state: this.operationState,
      api: this.api,
      refresh: () => this.refresh(),
      idToken,
    });
  }

  @action.bound
  async linkAppleFromIdToken(idToken: string): Promise<void> {
    await linkAppleIdentity({
      state: this.operationState,
      api: this.api,
      refresh: () => this.refresh(),
      idToken,
    });
  }

  @action.bound
  async unlinkIdentity(provider: AuthIdentity["provider"]): Promise<void> {
    await unlinkAuthIdentity({
      state: this.operationState,
      api: this.api,
      refresh: () => this.refresh(),
      provider,
    });
  }

  @action.bound
  isProviderProcessing(provider: AuthIdentity["provider"]): boolean {
    return this.processingProvider === provider;
  }

  providerLabel(provider: AuthIdentity["provider"]): string {
    return getAuthIdentityProviderLabel(provider);
  }

  private getCurrentUserId(): string | null {
    const user = getStoredUser();
    if (!user?.id) {
      return null;
    }

    return user.id;
  }

  private get operationState(): AccountOperationState {
    return this;
  }
}
